import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin/verify")({
  component: AdminVerifyPage,
});

type Check = { label: string; ok: boolean | null; detail: string };

const ADMIN_ROUTES = [
  "/admin",
  "/admin/users",
  "/admin/deposits",
  "/admin/withdrawals",
  "/admin/plans",
  "/admin/announcements",
  "/admin/verify",
] as const;

function AdminVerifyPage() {
  const [userId, setUserId] = useState<string>("…");
  const [email, setEmail] = useState<string>("…");
  const [roles, setRoles] = useState<string[]>([]);
  const [checks, setChecks] = useState<Check[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [routeChecks, setRouteChecks] = useState<Check[]>([]);
  const [queryChecks, setQueryChecks] = useState<Check[]>([]);

  async function runFullAccessTest() {
    setRunning(true);
    setRouteChecks([]);
    setQueryChecks([]);
    const { data: u } = await supabase.auth.getUser();
    const user = u.user;
    if (!user) {
      setRunning(false);
      return;
    }

    // 1. Confirm admin role (gate every admin route depends on)
    const { data: gateRow, error: gateErr } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .maybeSingle();
    const isAdmin = !gateErr && !!gateRow;

    const rChecks: Check[] = ADMIN_ROUTES.map((path) => ({
      label: `Route ${path}`,
      ok: isAdmin,
      detail: isAdmin
        ? "admin gate passes — route would render"
        : gateErr
          ? `gate query failed: ${gateErr.message}`
          : "admin role missing — route would redirect to /dashboard",
    }));
    setRouteChecks(rChecks);

    // 2. Real queries each admin page performs
    const qChecks: Check[] = [];

    const dep = await supabase
      .from("deposits")
      .select("id, user_id, amount, status, created_at")
      .order("created_at", { ascending: false })
      .limit(5);
    qChecks.push({
      label: "deposits — list with details (admin policy)",
      ok: !dep.error,
      detail: dep.error ? dep.error.message : `fetched ${dep.data?.length ?? 0} row(s)`,
    });

    const wd = await supabase
      .from("withdrawals")
      .select("id, user_id, amount, status, created_at")
      .order("created_at", { ascending: false })
      .limit(5);
    qChecks.push({
      label: "withdrawals — list with details (admin policy)",
      ok: !wd.error,
      detail: wd.error ? wd.error.message : `fetched ${wd.data?.length ?? 0} row(s)`,
    });

    const usersQ = await supabase
      .from("profiles")
      .select("id, email", { count: "exact" })
      .limit(5);
    qChecks.push({
      label: "profiles — admin user list",
      ok: !usersQ.error,
      detail: usersQ.error ? usersQ.error.message : `visible ${usersQ.count ?? usersQ.data?.length ?? 0}`,
    });

    const plansQ = await supabase.from("plans").select("id, name").limit(5);
    qChecks.push({
      label: "plans — admin list",
      ok: !plansQ.error,
      detail: plansQ.error ? plansQ.error.message : `fetched ${plansQ.data?.length ?? 0} row(s)`,
    });

    const annQ = await supabase.from("announcements").select("id, title").limit(5);
    qChecks.push({
      label: "announcements — admin list",
      ok: !annQ.error,
      detail: annQ.error ? annQ.error.message : `fetched ${annQ.data?.length ?? 0} row(s)`,
    });

    setQueryChecks(qChecks);
    setRunning(false);
  }

  useEffect(() => {
    (async () => {
      const next: Check[] = [];
      const { data: u } = await supabase.auth.getUser();
      const user = u.user;
      if (!user) {
        setLoading(false);
        return;
      }
      setUserId(user.id);
      setEmail(user.email ?? "—");

      const { data: roleRows, error: roleErr } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      const roleList = (roleRows ?? []).map((r) => r.role as string);
      setRoles(roleList);
      next.push({
        label: "user_roles SELECT (RLS allows reading own roles)",
        ok: !roleErr,
        detail: roleErr ? roleErr.message : `Returned ${roleList.length} row(s)`,
      });
      next.push({
        label: "has 'admin' role row",
        ok: roleList.includes("admin"),
        detail: roleList.includes("admin") ? "admin row present" : "no admin row found — run the SQL insert",
      });

      const { data: rpcRes, error: rpcErr } = await supabase.rpc("has_role", {
        _user_id: user.id,
        _role: "admin",
      });
      next.push({
        label: "has_role(uid, 'admin') RPC",
        ok: rpcErr ? false : Boolean(rpcRes),
        detail: rpcErr ? rpcErr.message : `returned ${String(rpcRes)}`,
      });

      const { data: depAll, error: depErr } = await supabase
        .from("deposits")
        .select("id", { count: "exact", head: true });
      next.push({
        label: "deposits SELECT under admin policy",
        ok: !depErr,
        detail: depErr ? depErr.message : `query succeeded (${depAll === null ? "head" : "rows"})`,
      });

      const { error: profErr, count } = await supabase
        .from("profiles")
        .select("id", { count: "exact", head: true });
      next.push({
        label: "profiles SELECT (admin can see all)",
        ok: !profErr,
        detail: profErr ? profErr.message : `visible profiles: ${count ?? 0}`,
      });

      setChecks(next);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-white/10 bg-card/40 p-4">
        <h2 className="text-sm font-medium text-muted-foreground">Identity</h2>
        <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-[160px_1fr]">
          <dt className="text-muted-foreground">Auth user id</dt>
          <dd className="font-mono break-all">{userId}</dd>
          <dt className="text-muted-foreground">Email</dt>
          <dd className="font-mono break-all">{email}</dd>
          <dt className="text-muted-foreground">Roles</dt>
          <dd>
            {roles.length === 0 ? (
              <span className="text-muted-foreground">none</span>
            ) : (
              roles.map((r) => (
                <span key={r} className="mr-2 inline-block rounded bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
                  {r}
                </span>
              ))
            )}
          </dd>
        </dl>
      </section>

      <section className="rounded-lg border border-white/10 bg-card/40 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-medium text-muted-foreground">Full admin access test</h2>
            <p className="text-xs text-muted-foreground">
              Uses your current session to confirm every admin route gate passes and that
              deposits/withdrawals and other admin queries succeed.
            </p>
          </div>
          <Button size="sm" onClick={runFullAccessTest} disabled={running}>
            {running ? "Running…" : "Run access test"}
          </Button>
        </div>

        {(routeChecks.length > 0 || queryChecks.length > 0) && (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Admin routes
              </h3>
              <ul className="space-y-2 text-sm">
                {routeChecks.map((c) => (
                  <li key={c.label} className="flex items-start gap-3 rounded border border-white/5 bg-background/40 p-2">
                    <span
                      className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        c.ok ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
                      }`}
                    >
                      {c.ok ? "✓" : "✕"}
                    </span>
                    <div>
                      <div className="font-medium">{c.label}</div>
                      <div className="text-xs text-muted-foreground break-all">{c.detail}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Admin queries
              </h3>
              <ul className="space-y-2 text-sm">
                {queryChecks.map((c) => (
                  <li key={c.label} className="flex items-start gap-3 rounded border border-white/5 bg-background/40 p-2">
                    <span
                      className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        c.ok ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
                      }`}
                    >
                      {c.ok ? "✓" : "✕"}
                    </span>
                    <div>
                      <div className="font-medium">{c.label}</div>
                      <div className="text-xs text-muted-foreground break-all">{c.detail}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </section>

      <section className="rounded-lg border border-white/10 bg-card/40 p-4">
        <h2 className="text-sm font-medium text-muted-foreground">RLS checks</h2>
        {loading ? (
          <p className="mt-3 text-sm text-muted-foreground">Running checks…</p>
        ) : (
          <ul className="mt-3 space-y-2 text-sm">
            {checks.map((c) => (
              <li key={c.label} className="flex items-start gap-3 rounded border border-white/5 bg-background/40 p-3">
                <span
                  className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                    c.ok ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
                  }`}
                >
                  {c.ok ? "✓" : "✕"}
                </span>
                <div>
                  <div className="font-medium">{c.label}</div>
                  <div className="text-xs text-muted-foreground break-all">{c.detail}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-xs text-muted-foreground">
        If any check fails, copy your auth user id above and run:
        <code className="ml-1 rounded bg-muted px-1 py-0.5 font-mono text-[11px]">
          INSERT INTO public.user_roles(user_id, role) VALUES ('{userId}', 'admin');
        </code>
      </p>
    </div>
  );
}